import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { StatCard } from './components/StatCard';
import { fmtMoney, fmtNum, fmtMoneyShort, fmtPeriodo } from './components/kpiUtils';
import {
  DollarSign, TrendingUp, Users, ArrowRight, Download, Gift, Activity, Store,
  UserCheck, ShieldBan, UserPlus, BookOpen, ArrowRightLeft, Gauge,
} from 'lucide-react';

const KpisHome = () => {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/kpis/resumen').then((r) => setResumen(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const stats = [
    { key: 'sum-ing', label: 'Ingresos históricos', icon: DollarSign, accent: '#4f46e5',
      value: loading ? '…' : fmtMoneyShort(resumen?.ingresos?.total_historico),
      deltaLabel: resumen?.ingresos?.ultimo_periodo ? `Últ. ${fmtPeriodo(resumen.ingresos.ultimo_periodo)}` : '',
      testId: 'kpi-summary-ingresos' },
    { key: 'sum-desc', label: 'Descargas totales', icon: Download, accent: '#0ea5e9',
      value: loading ? '…' : fmtNum(resumen?.descargas?.total),
      deltaLabel: resumen ? `iOS ${fmtNum(resumen.descargas?.ios)} · And ${fmtNum(resumen.descargas?.android)}` : '',
      testId: 'kpi-summary-descargas' },
    { key: 'sum-canjes', label: 'Canjes Bonda', icon: Gift, accent: '#10b981',
      value: loading ? '…' : fmtNum(resumen?.bonda?.canjes),
      deltaLabel: 'total', testId: 'kpi-summary-bonda' },
    { key: 'sum-users', label: 'Usuarios que canjean', icon: Users, accent: '#3b82f6',
      value: loading ? '…' : fmtNum(resumen?.bonda?.usuarios_unicos),
      deltaLabel: 'únicos', testId: 'kpi-summary-usuarios' },
  ];

  const cards = [
    { key: 'estado', titulo: 'Estado de datos', desc: 'Qué tan actualizada está cada sección de KPIs',
      icon: Gauge, color: '#6366f1', ruta: '/m/kpis/kpis-estado',
      metric: 'Ver estado', sub: 'Semáforo de actualización por sección' },
    { key: 'ingresos', titulo: 'Ingresos', desc: 'Carga manual mensual por fuente de negocio',
      icon: DollarSign, color: '#4f46e5', ruta: '/m/kpis/kpis-ingresos',
      metric: resumen ? fmtMoney(resumen.ingresos?.total_ultimo) : '—',
      sub: resumen?.ingresos?.ultimo_periodo ? `Último período · ${fmtPeriodo(resumen.ingresos.ultimo_periodo)}` : 'Sin datos' },
    { key: 'adquisicion', titulo: 'Descargas', desc: 'Descargas de la app en iOS y Android',
      icon: Download, color: '#0ea5e9', ruta: '/m/kpis/kpis-adquisicion',
      metric: resumen ? fmtNum(resumen.descargas?.total) : '—',
      sub: resumen ? `iOS ${fmtNum(resumen.descargas?.ios)} · Android ${fmtNum(resumen.descargas?.android)}` : 'Sin datos' },
    { key: 'adopcion', titulo: 'Bonda', desc: 'Uso de beneficios Bonda (canjes de cupones)',
      icon: Gift, color: '#10b981', ruta: '/m/kpis/kpis-adopcion',
      metric: resumen ? fmtNum(resumen.bonda?.canjes) : '—',
      sub: resumen ? `${fmtNum(resumen.bonda?.usuarios_unicos)} usuarios únicos` : 'Sin datos' },
    { key: 'clientes', titulo: 'Clientes activos', desc: 'MAU: clientes con CVU activos y su retención',
      icon: UserCheck, color: '#6366f1', ruta: '/m/kpis/kpis-clientes-activos',
      metric: '—', sub: 'Reporte de billeteras (Waya)' },
    { key: 'transaccionalidad', titulo: 'Transaccionalidad', desc: 'Movimientos por mes: Ingresos vs Egresos (cantidad y montos)',
      icon: ArrowRightLeft, color: '#4f46e5', ruta: '/m/kpis/kpis-transaccionalidad',
      metric: '—', sub: 'Base de movimientos (Waya)' },
    { key: 'bloqueos', titulo: 'Bloqueos', desc: 'Bloqueos por límites, por monitoreo transaccional y ROS',
      icon: ShieldBan, color: '#f43f5e', ruta: '/m/kpis/kpis-bloqueos',
      metric: '—', sub: 'Compliance de bloqueos' },
    { key: 'afiliacion', titulo: 'Afiliación', desc: 'Padrón de afiliados: altas y demografía',
      icon: UserPlus, color: '#7c3aed', ruta: '/m/kpis/kpis-afiliacion',
      metric: '—', sub: 'Altas por mes' },
    { key: 'glosario', titulo: 'Glosario', desc: 'Cómo se calcula cada métrica del módulo',
      icon: BookOpen, color: '#f59e0b', ruta: '/m/kpis/kpis-glosario',
      metric: '—', sub: 'Definiciones y fórmulas' },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="kpis-home">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1.5">
          <div className="p-2.5 rounded-xl bg-indigo-500/10">
            <Activity className="h-6 w-6 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">KPIs</h1>
        </div>
        <p className="text-slate-500 dark:text-slate-400">
          KPIs de negocio: Ingresos, Descargas, Bonda, Clientes, Bloqueos y Afiliación
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
        {stats.map((s) => (
          <StatCard key={s.key} label={s.label} value={s.value} icon={s.icon} accent={s.accent} deltaLabel={s.deltaLabel} testId={s.testId} />
        ))}
      </div>

      <h2 className="text-xl font-semibold tracking-tight text-slate-800 dark:text-slate-200 mb-4">Módulos</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.key}
              onClick={() => navigate(c.ruta)}
              className="p-6 cursor-pointer border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 ease-out group"
              data-testid={`kpi-card-${c.key}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: `${c.color}1a` }}>
                  <Icon className="h-6 w-6" style={{ color: c.color }} />
                </div>
                <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-[transform,color] duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{c.titulo}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{c.desc}</p>
              <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                <p className="text-2xl font-bold tracking-tighter" style={{ color: c.color }}>{loading ? '…' : c.metric}</p>
                <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default KpisHome;
